// src/pages/clubs/ClubDashboard.jsx
// Club-level overview: all teams with live registration stats, members, quick actions.
// Route: /clubs/:clubId

import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  collection, doc, getDoc, getDocs, query, where, limit, getCountFromServer,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import useClub from '@/hooks/useClub';
import PageSpinner from '@/components/PageSpinner';
import clsx from 'clsx';
import { endOfSeasonDay } from '@/utils/seasonDates';

import {
  BuildingOffice2Icon,
  UserGroupIcon,
  Cog6ToothIcon,
  ChevronRightIcon,
  TrophyIcon,
  ShieldCheckIcon,
  PlusIcon,
  MegaphoneIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  RectangleGroupIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import useClubPrograms from '@/hooks/useClubPrograms';
import ClubRoleBadge from '@/components/ClubRoleBadge';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ------------------------------------------------------------------ */
/* Stat card                                                            */
/* ------------------------------------------------------------------ */
function StatCard({ icon: Icon, label, value, color = 'lime' }) {
  const colors = {
    lime:    'bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f]',
    blue:    'bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f]',
    violet:  'bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f]',
    emerald: 'bg-emerald-50 ring-1 ring-emerald-200 text-emerald-600',
    orange:  'bg-orange-50 ring-1 ring-orange-200 text-orange-600',
  };
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5 flex items-center gap-4">
      <div className={clsx('rounded-xl p-3 flex-shrink-0', colors[color])}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>{value}</p>
        <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Team card with registration stats                                   */
/* ------------------------------------------------------------------ */
function TeamCard({ team, stats }) {
  const navigate = useNavigate();
  const hasIssues = (stats?.unpaid ?? 0) > 0 || (stats?.missingDocs ?? 0) > 0;
  // Drill-down link: clicking unpaid/missing-docs badges should go
  // straight to the team's registrations page, not the generic team
  // page. Falls back to team page if seasonId isn't loaded yet.
  const regsUrl = stats?.seasonId
    ? `/seasons/${stats.seasonId}/registrations`
    : `/teams/${team.id}`;

  // Stop the parent Link from firing when a badge is clicked, then
  // route to the deeper destination. Avoids restructuring the card
  // (the whole row is the team link by design).
  const goToRegs = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(regsUrl);
  };

  return (
    <Link
      to={`/teams/${team.id}`}
      className="group rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-4 hover:border-[#b5f03d]/40 hover:shadow-sm transition-all block"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center mt-0.5">
          <TrophyIcon className="h-5 w-5 text-[#5a8a1f]" />
        </div>

        {/* Name & sport */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-[#0d1f14] truncate">{team.name}</p>
            {hasIssues && (
              <button
                type="button"
                onClick={goToRegs}
                className="inline-flex items-center gap-0.5 rounded-full bg-orange-100 hover:bg-orange-200 px-2 py-0.5 text-[10px] font-bold text-orange-700 transition-colors cursor-pointer"
                title="Open registrations"
              >
                <ExclamationCircleIcon className="h-3 w-3" />
                Action needed
              </button>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">
            {[team.sport, team.ageGroup, team.gender].filter(Boolean).join(' · ') || 'Team'}
          </p>

          {/* Season / registration stats row */}
          {stats && (
            <div className="flex flex-wrap gap-2 mt-2">
              {stats.seasonName && (
                <span className="inline-flex items-center gap-1 text-[11px] text-zinc-400">
                  <ClockIcon className="h-3 w-3" />
                  {stats.seasonName}
                </span>
              )}
              {stats.registered > 0 && (
                <button
                  type="button"
                  onClick={goToRegs}
                  className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium hover:underline"
                  title="Open registrations"
                >
                  <CheckCircleIcon className="h-3 w-3" />
                  {stats.registered} registered
                </button>
              )}
              {stats.unpaid > 0 && (
                <button
                  type="button"
                  onClick={goToRegs}
                  className="inline-flex items-center gap-1 text-[11px] text-orange-600 font-semibold hover:underline"
                  title="View unpaid registrations"
                >
                  <ExclamationCircleIcon className="h-3 w-3" />
                  {stats.unpaid} unpaid
                </button>
              )}
              {stats.missingDocs > 0 && (
                <button
                  type="button"
                  onClick={goToRegs}
                  className="inline-flex items-center gap-1 text-[11px] text-red-500 font-semibold hover:underline"
                  title="View registrations missing documents"
                >
                  <ExclamationCircleIcon className="h-3 w-3" />
                  {stats.missingDocs} missing docs
                </button>
              )}
              {stats.registered === 0 && !stats.seasonName && (
                <span className="text-[11px] text-zinc-400 italic">No active season</span>
              )}
            </div>
          )}
        </div>

        <ChevronRightIcon className="h-4 w-4 text-zinc-400 group-hover:text-[#5a8a1f] transition-colors flex-shrink-0 mt-1" />
      </div>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */
export default function ClubDashboard() {
  const { clubId } = useParams();
  const { firebaseUser } = useAuth();
  const { club, members, myRole, isAdmin, loading } = useClub(clubId);

  const [teams, setTeams]           = useState([]);
  const [teamStats, setTeamStats]   = useState({});   // teamId → { seasonName, registered, unpaid, missingDocs }
  const [teamsLoading, setTeamsLoading] = useState(true);

  const { programs, loading: programsLoading } = useClubPrograms(clubId);
  const [programRegCounts, setProgramRegCounts] = useState({}); // { programId: number }

  // Fetch registration counts per program (informational badge).
  //
  // Uses getDocs+size instead of getCountFromServer because the
  // registrations rule has a per-doc `parentEmail.lower() ==
  // request.auth.token.email` branch that aggregation queries
  // can't statically evaluate, returning 403 even when clubId is
  // correctly constrained. getDocs uses per-doc rule evaluation
  // which works. Slight extra read cost is fine at current scale;
  // promote to a counter doc + Cloud Function trigger if program
  // registrations ever grow into the hundreds per program.
  useEffect(() => {
    if (!clubId || !programs.length) return;
    programs.forEach(async (p) => {
      try {
        const snap = await getDocs(
          query(
            collection(db, 'registrations'),
            where('clubId',    '==', clubId),
            where('programId', '==', p.id),
          )
        );
        setProgramRegCounts((prev) => ({ ...prev, [p.id]: snap.size }));
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[ClubDashboard] program count failed', p.id, e);
      }
    });
  }, [clubId, programs.map(p => p.id).join(',')]);

  // Load teams
  useEffect(() => {
    if (!club?.teamIds?.length) { setTeams([]); setTeamsLoading(false); return; }
    setTeamsLoading(true);
    const ids = club.teamIds.filter(Boolean);
    Promise.all(ids.map(id => getDoc(doc(db, 'teams', id))))
      .then(snaps => setTeams(snaps.filter(s => s.exists()).map(s => ({ id: s.id, ...s.data() }))))
      .catch(err => console.error('[ClubDashboard] teams error', err))
      .finally(() => setTeamsLoading(false));
  }, [club?.teamIds?.join(',')]);

  // Load registration stats per team
  useEffect(() => {
    if (!teams.length) return;

    const loadStats = async () => {
      const results = {};

      await Promise.all(teams.map(async (team) => {
        try {
          // Get the active season for this team. Real seasons (and the
          // demo seeder) use `status === 'open'` — no code path ever
          // writes `isActive`, which is why this stat was permanently 0
          // for every club.
          const seasonSnap = await getDocs(
            query(
              collection(db, 'seasons'),
              where('teamId', '==', team.id),
              where('status', '==', 'open'),
              limit(1)
            )
          );

          if (seasonSnap.empty) {
            results[team.id] = { seasonName: null, registered: 0, unpaid: 0, missingDocs: 0 };
            return;
          }

          const season = { id: seasonSnap.docs[0].id, ...seasonSnap.docs[0].data() };
          const requiredDocs = season.requiredDocTypes || [];

          // Get all registrations for this season. Must include teamId
          // to satisfy the registrations rule (gates on isTeamStaff(teamId));
          // a seasonId-only query fails permission-denied because the rule
          // wraps teamId in a function call which isn't pattern-matchable.
          const regsSnap = await getDocs(
            query(
              collection(db, 'registrations'),
              where('seasonId', '==', season.id),
              where('teamId',   '==', team.id),
            )
          );

          let registered = 0, unpaid = 0, missingDocs = 0;
          regsSnap.docs.forEach(d => {
            const r = d.data();
            if (r.status === 'cancelled') return;
            registered++;
            if ((r.amountDue || 0) > 0 && !['paid', 'confirmed'].includes(r.status)) unpaid++;
            if (requiredDocs.length > 0) {
              const uploaded = r.uploadedDocs || {};
              if (requiredDocs.some(dt => !uploaded[dt])) missingDocs++;
            }
          });

          // daysUntilClose — drives the "registration closing soon"
          // director rollup signal. Same calc as TeamSmartActions on
          // the per-team page; lifted here so the club director can
          // see closing-soon teams without tapping into each one.
          let daysUntilClose = null;
          if (season.registrationClose) {
            try {
              const closeDate = endOfSeasonDay(season.registrationClose);
              const days = Math.ceil((closeDate - Date.now()) / (1000 * 60 * 60 * 24));
              daysUntilClose = days > 0 ? days : 0;
            } catch { /* ignore */ }
          }

          results[team.id] = {
            seasonId: season.id,
            seasonName: season.name || season.year || null,
            registered,
            unpaid,
            missingDocs,
            daysUntilClose,
            maxPlayers: season.maxPlayers || null,
          };
        } catch (err) {
          console.error('[ClubDashboard] stats error for team', team.id, err);
          results[team.id] = { seasonId: null, seasonName: null, registered: 0, unpaid: 0, missingDocs: 0 };
        }
      }));

      setTeamStats(results);
    };

    loadStats();
  }, [teams.map(t => t.id).join(',')]);

  if (loading) return <PageSpinner />;

  if (!club) {
    return (
      <div className="min-h-screen bg-[#f7faf5] flex flex-col items-center justify-center text-center px-4">
        <BuildingOffice2Icon className="h-12 w-12 text-zinc-300 mb-4" />
        <h2 className="text-lg font-semibold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Club not found</h2>
        <p className="text-sm text-zinc-500 mt-1 mb-4">This club may have been removed or you don't have access.</p>
        <Link to="/dashboard" className="text-sm text-[#5a8a1f] hover:text-[#3d6010] hover:underline">← Back to Dashboard</Link>
      </div>
    );
  }

  const memberRole = myRole || (club.ownerId === firebaseUser?.uid ? 'owner' : 'member');

  // Cross-team totals
  const totalRegistered = Object.values(teamStats).reduce((s, t) => s + (t.registered || 0), 0);
  const totalUnpaid     = Object.values(teamStats).reduce((s, t) => s + (t.unpaid || 0), 0);
  const teamsNeedingAttention = teams.filter(t => {
    const s = teamStats[t.id];
    return s && ((s.unpaid || 0) > 0 || (s.missingDocs || 0) > 0);
  }).length;

  return (
    <div className="min-h-screen bg-[#f7faf5]">
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">

      {/* ── Pitch-dark hero ────────────────────────── */}
      <div
        className="relative overflow-hidden rounded-2xl bg-[#0d1f14] p-6 sm:p-7 shadow-[0_2px_6px_0_rgba(13,31,20,0.18)]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(181,240,61,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.06) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      >
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {club.logoUrl ? (
              <img
                src={club.logoUrl}
                alt={club.name}
                className="h-16 w-16 rounded-2xl object-contain bg-white p-1 ring-2 ring-[#b5f03d]/40 flex-shrink-0"
              />
            ) : (
              <div className="h-16 w-16 rounded-2xl bg-[#b5f03d] ring-2 ring-[#b5f03d]/30 flex items-center justify-center flex-shrink-0">
                <BuildingOffice2Icon className="h-8 w-8 text-[#0d1f14]" />
              </div>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#b5f03d]" style={{ fontFamily: HEAD_FONT }}>Club</p>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <h1 className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: HEAD_FONT }}>{club.name}</h1>
                <ClubRoleBadge role={memberRole} />
              </div>
              <p className="text-sm text-zinc-300 mt-1">
                {[club.sport, `${members.length} member${members.length !== 1 ? 's' : ''}`, `${(club.teamIds || []).length} team${(club.teamIds || []).length !== 1 ? 's' : ''}`].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>

          {isAdmin && (
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/clubs/${clubId}/broadcast`}
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3.5 py-2 text-xs font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                <MegaphoneIcon className="h-4 w-4" />
                Broadcast
              </Link>
              <Link
                to={`/clubs/${clubId}/settings`}
                className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/5 px-3.5 py-2 text-xs font-bold uppercase text-white hover:bg-white/10 transition-all"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <Cog6ToothIcon className="h-4 w-4" />
                Settings
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* ── Quick links ────────────────────────────── */}
      {isAdmin && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Link
            to={`/clubs/${clubId}/members`}
            className="group rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-4 hover:border-[#b5f03d]/40 transition-all flex items-center gap-3"
          >
            <div className="h-10 w-10 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0">
              <UserGroupIcon className="h-5 w-5 text-[#5a8a1f]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Members</p>
              <p className="text-xs text-zinc-500">Invite & manage roles</p>
            </div>
            <ChevronRightIcon className="h-4 w-4 text-zinc-300 group-hover:text-[#5a8a1f] transition-colors" />
          </Link>
          <Link
            to="/reports/financial"
            className="group rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-4 hover:border-[#b5f03d]/40 transition-all flex items-center gap-3"
          >
            <div className="h-10 w-10 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0">
              <ChartBarIcon className="h-5 w-5 text-[#5a8a1f]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Financials</p>
              <p className="text-xs text-zinc-500">Reports & receivables</p>
            </div>
            <ChevronRightIcon className="h-4 w-4 text-zinc-300 group-hover:text-[#5a8a1f] transition-colors" />
          </Link>
          <Link
            to={`/clubs/${clubId}/settings`}
            className="group rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-4 hover:border-[#b5f03d]/40 transition-all flex items-center gap-3"
          >
            <div className="h-10 w-10 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0">
              <Cog6ToothIcon className="h-5 w-5 text-[#5a8a1f]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Settings</p>
              <p className="text-xs text-zinc-500">Edit club details</p>
            </div>
            <ChevronRightIcon className="h-4 w-4 text-zinc-300 group-hover:text-[#5a8a1f] transition-colors" />
          </Link>
          <Link
            to={`/clubs/${clubId}/website`}
            className="group rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-4 hover:border-[#b5f03d]/40 transition-all flex items-center gap-3"
          >
            <div className="h-10 w-10 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0">
              <GlobeAltIcon className="h-5 w-5 text-[#5a8a1f]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Website</p>
              <p className="text-xs text-zinc-500">Edit your club site</p>
            </div>
            <ChevronRightIcon className="h-4 w-4 text-zinc-300 group-hover:text-[#5a8a1f] transition-colors" />
          </Link>
        </div>
      )}

      {/* ── Attention banner ──────────────────────── */}
      {/* Director Smart Actions rollup — restored May 2026. The old
          single-line "N teams need attention" banner just told the
          director there was a problem somewhere; this version itemizes
          per team so they can act per team or use Broadcast for the
          common case. Surfaces:
            - teams with registration closing in ≤7 days (urgent at ≤2)
            - teams with unpaid registrations
            - teams with missing required documents
          Each row links to that team's page where the per-team Smart
          Actions stack handles the resolution. The Broadcast CTA at
          the top remains as a quick path for "everyone, please pay."
          Renders null if no team has any pending action — no nag. */}
      {(() => {
        const rollupRows = teams
          .map((t) => {
            const s = teamStats[t.id] || {};
            const issues = [];
            if (s.daysUntilClose != null && s.daysUntilClose <= 7 && s.daysUntilClose > 0) {
              issues.push({
                kind: 'closing',
                urgent: s.daysUntilClose <= 2,
                label: `Registration closes in ${s.daysUntilClose} day${s.daysUntilClose === 1 ? '' : 's'}`,
              });
            }
            if ((s.unpaid || 0) > 0) {
              issues.push({
                kind: 'unpaid',
                urgent: false,
                label: `${s.unpaid} unpaid registration${s.unpaid === 1 ? '' : 's'}`,
              });
            }
            if ((s.missingDocs || 0) > 0) {
              issues.push({
                kind: 'missingDocs',
                urgent: false,
                label: `${s.missingDocs} missing document${s.missingDocs === 1 ? '' : 's'}`,
              });
            }
            return issues.length > 0 ? { team: t, issues, seasonName: s.seasonName } : null;
          })
          .filter(Boolean);

        if (rollupRows.length === 0) return null;

        return (
          <div className="rounded-2xl border border-orange-200 bg-orange-50/60 overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-orange-200/60 bg-orange-50">
              <div className="flex items-center gap-2 min-w-0">
                <ExclamationCircleIcon className="h-4 w-4 text-orange-600 shrink-0" />
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-orange-800"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Action needed across {rollupRows.length} team{rollupRows.length === 1 ? '' : 's'}
                </p>
              </div>
              <Link
                to={`/clubs/${clubId}/broadcast`}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 px-2.5 py-1 text-[11px] font-bold uppercase text-white transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <MegaphoneIcon className="h-3 w-3" />
                Broadcast
              </Link>
            </div>
            <ul className="divide-y divide-orange-200/40">
              {rollupRows.map(({ team, issues, seasonName }) => {
                const hasUrgent = issues.some((i) => i.urgent);
                return (
                  <li key={team.id}>
                    <Link
                      to={`/teams/${team.id}`}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-100/60 transition-colors group"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#0d1f14] truncate" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.2px' }}>
                          {team.name}
                          {seasonName && (
                            <span className="ml-2 text-[10px] font-normal text-zinc-500 normal-case tracking-normal">
                              {seasonName}
                            </span>
                          )}
                        </p>
                        <p className="text-xs mt-0.5 truncate">
                          {issues.map((i, idx) => (
                            <React.Fragment key={i.kind}>
                              {idx > 0 && <span className="text-zinc-400"> · </span>}
                              <span className={i.urgent ? 'font-semibold text-red-700' : 'text-orange-700'}>
                                {i.label}
                              </span>
                            </React.Fragment>
                          ))}
                        </p>
                      </div>
                      {hasUrgent && (
                        <span
                          className="shrink-0 inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.6px] text-white"
                          style={{ fontFamily: HEAD_FONT }}
                        >
                          Urgent
                        </span>
                      )}
                      <ChevronRightIcon className="h-4 w-4 shrink-0 text-orange-300 group-hover:text-orange-700 transition-colors" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })()}

      {/* ── Stats row ──────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={TrophyIcon}              label="Teams"      value={(club.teamIds || []).length} color="lime" />
        <StatCard icon={UserGroupIcon}           label="Members"    value={members.length}              color="lime" />
        <StatCard icon={CheckCircleIcon}         label="Registered" value={totalRegistered}             color="emerald" />
        <StatCard icon={ExclamationCircleIcon}   label="Unpaid"     value={totalUnpaid}                 color="orange" />
      </div>

      {/* ── Programs ───────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>Programs</h2>
          <Link
            to={`/clubs/${clubId}/programs/create`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#5a8a1f] hover:text-[#3d6010]"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            New Program
          </Link>
        </div>

        {programsLoading ? (
          <div className="h-16 flex items-center justify-center text-zinc-400 text-sm">Loading…</div>
        ) : programs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-zinc-300 p-6 text-center bg-white">
            <RectangleGroupIcon className="h-8 w-8 text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No programs yet.</p>
            <Link
              to={`/clubs/${clubId}/programs/create`}
              className="mt-1 inline-block text-sm text-[#5a8a1f] hover:underline"
            >
              Create a registration program →
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {programs.slice(0, 4).map((p) => {
              const STATUS_CLS = {
                draft:    'bg-zinc-100 text-zinc-500',
                open:     'bg-emerald-50 text-emerald-700',
                closed:   'bg-red-50 text-red-600',
                archived: 'bg-zinc-100 text-zinc-400',
              };
              const divCount = p.divisions?.length ?? 0;
              return (
                <Link
                  key={p.id}
                  to={`/clubs/${clubId}/programs/${p.id}`}
                  className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-4 py-3 hover:border-[#b5f03d]/40 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <RectangleGroupIcon className="h-4 w-4 text-[#5a8a1f] shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#0d1f14] truncate group-hover:text-[#3d6010]">
                        {p.name}
                      </p>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        {divCount} division{divCount !== 1 ? 's' : ''}
                        {p.sport ? ` · ${p.sport}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {programRegCounts[p.id] != null && (
                      <span className="text-[11px] text-zinc-400 font-medium">
                        {programRegCounts[p.id]} reg{programRegCounts[p.id] !== 1 ? 's' : ''}
                      </span>
                    )}
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[p.status] ?? STATUS_CLS.draft}`}>
                      {p.status === 'open' ? 'Open' : p.status === 'closed' ? 'Closed' : p.status === 'archived' ? 'Archived' : 'Draft'}
                    </span>
                    <ChevronRightIcon className="h-4 w-4 text-zinc-300 group-hover:text-[#5a8a1f] transition-colors" />
                  </div>
                </Link>
              );
            })}
            {programs.length > 4 && (
              <Link
                to={`/clubs/${clubId}/programs`}
                className="block text-center text-xs font-semibold text-[#5a8a1f] hover:underline py-2"
              >
                View all {programs.length} programs →
              </Link>
            )}
          </div>
        )}
      </div>

      {/* ── Teams ──────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>Teams</h2>
          {/* Pre-fill the new team's club via URL param so the coach
              doesn't have to re-pick this club from the autocomplete. */}
          <Link to={`/teams/create?clubId=${clubId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#5a8a1f] hover:text-[#3d6010]">
            <PlusIcon className="h-3.5 w-3.5" />
            Add Team
          </Link>
        </div>

        {teamsLoading ? (
          <div className="flex items-center justify-center h-24 text-zinc-400 text-sm">Loading teams…</div>
        ) : teams.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-zinc-300 p-8 text-center bg-white">
            <TrophyIcon className="h-8 w-8 text-zinc-300 mx-auto mb-2" />
            <p
              className="text-sm font-bold uppercase text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              No teams yet
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              Already coaching teams elsewhere? Attach them here so they share this club's branding, broadcasts, and reports.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
              <Link
                to={`/clubs/${clubId}/settings`}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                Attach existing teams →
              </Link>
              <Link
                to={`/teams/create?clubId=${clubId}`}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                Or create your first team
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {teams.map(team => (
              <TeamCard key={team.id} team={team} stats={teamStats[team.id] ?? null} />
            ))}
          </div>
        )}
      </div>

      {/* ── Members preview ────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>Members</h2>
          <Link to={`/clubs/${clubId}/members`} className="text-xs font-semibold text-[#5a8a1f] hover:text-[#3d6010]">See all →</Link>
        </div>
        {members.length === 0 ? (
          <p className="text-sm text-zinc-500">No members yet.</p>
        ) : (
          <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] divide-y divide-zinc-100 overflow-hidden">
            {members.slice(0, 5).map(m => (
              <div key={m.uid} className="flex items-center gap-3 px-4 py-3">
                <div className="h-8 w-8 rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-[#5a8a1f] uppercase">
                    {(m.displayName || m.email || '?')[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0d1f14] truncate">{m.displayName || m.email}</p>
                  {m.displayName && <p className="text-xs text-zinc-400 truncate">{m.email}</p>}
                </div>
                <ClubRoleBadge role={m.role} />
              </div>
            ))}
            {members.length > 5 && (
              <div className="px-4 py-3 text-center">
                <Link to={`/clubs/${clubId}/members`} className="text-xs text-[#5a8a1f] hover:underline font-semibold">
                  View all {members.length} members →
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
